//******************************************************************************
/**
 * @fileoverview RecordAnalyzer - DataPNGカラムデータ解析クラス
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @version 0.3.1
 * @since 2026-01-27
 * @copyright 2026 KITAO Kaoru
 * @license MIT
 * @requires DataPngBase
 * * @history
 * - 2026-02-24 v0.3.1 JSDoc整理
 * - 2026-02-23 v0.3.0 カラムデータ解析を集約
 * - 2026-02-03 v0.2.1 体裁調整
 * - 2026-01-28 v0.2.0 name属性の付与と取得のメソッド追加
 * - 2026-01-27 v0.1.0 リリース
 */

//******************************************************************************
// import
//------------------------------------------------------------------------------
// 継承元クラス
import { DataPngBase } from "./DataPngBase.js";

//******************************************************************************
/**
 * クラス定義
 * @extends DataPngBase
 */
class RecordAnalyzer extends DataPngBase {
	//**************************************************************************
	/**
	 * コンストラクタ
	 * @param {Uint8Array|Uint8ClampedArray} rgba カラム情報1ピクセル分のRGBA
	 */
	constructor(rgba) {
		//----------------------------------------------------------------------
		// 親クラスのメソッドを呼ぶ（親クラスは引数をとらない）
		super();
		//----------------------------------------------------------------------
		// レコード情報のカラム型に対する処理
		// カラムの型定義（1:rgba、2:uint、3:intが規定されているがundefinedを考慮する）
		const types = ["undefined", "rgba", "uint", "int"];
		// カラム型はRの先頭4ビットで決まる
		const recordType = (rgba[0] >> 4) & 0xf;
		// カラム型が決まる
		this.type = types[recordType];
		// 想定外の場合は例外を投げる
		if (typeof this.type === "undefined") {
			throw new Error(`Invalid record type ${recordType} specified.`);
		}
		//----------------------------------------------------------------------
		// レコード情報の1レコードあたりのピクセル数（Rの後方4ビット）
		this.recordPixelLength = rgba[0] & 0xf;
		// オフセットピクセル数（G値）
		this.offsetPixelCount = rgba[1];
		// 係数常用対数（符号付き整数に変換）
		this.factor = (rgba[2] << 24) >> 24;
		//----------------------------------------------------------------------
		// カラム情報1ピクセルだけからは決まらないメンバ変数を準備
		this.name = undefined;
		this.offset = 0;
		this.rawData = undefined;
	}

	//**************************************************************************
	/**
	 * オフセット値の計算
	 * @param {Uint8Array|Uint8ClampedArray} rgbaData オフセット用ピクセル
	 * @returns {this}
	 * @throws {Error} 適用すべきメソッドが見つからない場合
	 * @throws {Error} 引数エラー時は例外（設計的に順番が悪いが仕方ない）
	 */
	setOffsetPixelRgba(rgbaData) {
		//----------------------------------------------------------------------
		// オフセットピクセル長が0ならオフセットを0として停止（例外なく実行はする）
		if (this.offsetPixelCount === 0) {
			this.offset = 0;
			return this;
		}
		//----------------------------------------------------------------------
		// 数値型の場合のみ処理する（暫定処理・現状では破綻しないが将来不安）
		// レコード情報の型に合わせる（将来的に点群PNGの仕様として正しいか不明）
		if (this.type === "uint" || this.type === "int") {
			// メソッド名を決めるための型の準備
			const type = `${this.type.slice(0, 1).toUpperCase()}${this.type.slice(1).toLowerCase()}`;
			// ビット長はピクセル数*24
			const bitLength = this.offsetPixelCount * 24;
			// メソッド名を決める
			const method = `decode${type}${bitLength}`;
			// 規定されたメソッド名が有効でない場合は例外
			if (typeof this[method] !== "function") {
				throw new Error(`Method ${method} not defined.`);
			}
			// 値を計算
			const value = this[method](rgbaData);
			// RGBAデータの異常検知は例外
			if (value === null) {
				throw new Error(`Invalid alpha value 0 for offset; expected 255.`);
			}
			// オフセット値の格納
			this.offset = value;
		}
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * 当該カラムのレコード情報を格納する
	 * @param {Uint8Array|Uint8ClampedArray} rawData RGBA配列
	 * @returns {this}
	 */
	setRawRecords(rawData) {
		//----------------------------------------------------------------------
		// メンバ変数に値を保持する
		this.rawData = rawData;
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * 保持したレコード情報のRGBA配列をそのまま返す（外部でデコードするため）
	 * @returns {Uint8Array|Uint8ClampedArray} RGBA配列
	 */
	getRawRecords() {
		return this.rawData;
	}

	//**************************************************************************
	/**
	 * レコード情報をデコードして返す（点数が少なければこの処理で大丈夫）
	 * @returns {Float64Array|Array<Array<number>>}
	 * @throws {Error} 数値処理でRGBAのAが0のとき
	 */
	getDecodedRecords() {
		//----------------------------------------------------------------------
		// レコード数は外部で保持しているので計算で求めておく
		const recordCount = this.rawData.length / (
			this.ELEMENTS * this.recordPixelLength
		);
		//----------------------------------------------------------------------
		// 逐次処理のためのポインタ初期化
		this.pointer = 0;
		//----------------------------------------------------------------------
		// デコード結果が数値か否かで分岐
		if (this.type === "uint" || this.type === "int") {
			// 数値の場合
			// メソッド名を決めるための型の準備
			const type = `${this.type.slice(0, 1).toUpperCase()}${this.type.slice(1).toLowerCase()}`;
			// ビット長はピクセル数*24
			// const bitLength = this.offsetPixelCount * 24;
			const bitLength = this.recordPixelLength * 24;
			// メソッド名を決める
			const method = `decode${type}${bitLength}`;
			// 規定されたメソッド名が有効でない場合は例外
			if (typeof this[method] !== "function") {
				throw new Error(`Method ${method} not defined.`);
			}
			// ピクセル数で分岐せず一律64ビット浮動小数として格納する
			const records = new Float64Array(recordCount);
			// 1レコード単位ごとに走査
			for (let i = 0; i < recordCount; i += 1) {
				// 1レコード分のRGBAを取得してデコード
				const rgba = this.getRgbaData(this.recordPixelLength);
				const value = this[method](rgba);
				// nullは例外を投げる（暫定）
				if (value === null) {
					throw new Error(`Invalid alpha value 0 in record data.`);
				}
				// デコード結果にオフセットと係数常用対数を加味して格納
				records[i] = (value + this.offset) * 10 ** (this.factor * -1);
			}
			// 結果を返す
			return records;
		} else {
			// 数値以外の場合
			// メソッド名を決めるための型の準備
			const type = `${this.type.slice(0, 1).toUpperCase()}${this.type.slice(1).toLowerCase()}`;
			// メソッド名を決める
			const method = `decode${type}`;
			// 結果を格納する配列を準備（ここが数値との違い）
			const records = new Array(recordCount);
			// 1レコード単位ごとに走査
			for (let i = 0; i < recordCount; i += 1) {
				// 1レコード分のRGBAを取得する
				const rgba = this.getRgbaData(this.recordPixelLength);
				// デコード結果を格納（オフセットと係数常用対数の処理不要）
				records[i] = this[method](rgba);
			}
			// 結果を返す
			return records;
		}
	}

	//**************************************************************************
	/**
	 * カラム名を設定する
	 * @param {string|undefined} name
	 * @returns {this}
	 */
	setName(name) {
		this.name = name;
		return this;
	}

	//**************************************************************************
	/**
	 * カラム名を返す
	 * @returns {string|undefined}
	 */
	getName() {
		return this.name;
	}

	//**************************************************************************
	/**
	 * オフセットピクセル長を返す
	 * @returns {number}
	 */
	getOffsetPixelCount() {
		return this.offsetPixelCount;
	}

	//**************************************************************************
	/**
	 * レコードピクセル長を返す
	 * @returns {number}
	 */
	getRecordPixelLength() {
		return this.recordPixelLength;
	}

	//**************************************************************************
	/**
	 * オフセットを返す
	 * @returns {number}
	 */
	getOffset() {
		return this.offset;
	}

	//**************************************************************************
	/**
	 * 係数常用対数を返す
	 * @returns {number}
	 */
	getFactor() {
		return this.factor;
	}
}

//******************************************************************************
// export
export { RecordAnalyzer };
